import {
  DEFAULT_ANIMALS,
  DEFAULT_RATING,
  MAX_HISTORY,
  RATINGS,
  STORAGE_KEYS,
  type Rating,
} from './config';
import type { GiphyGif } from './types';

type Theme = 'dark' | 'light';
type View = 'browse' | 'favorites';

interface AppState {
  animals: string[];
  favorites: GiphyGif[];
  history: string[];
  rating: Rating;
  theme: Theme;
  view: View;
}

type Listener = (state: AppState) => void;

function load<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

function persist(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage full or unavailable — non-fatal */
  }
}

const state: AppState = {
  animals: load(STORAGE_KEYS.animals, DEFAULT_ANIMALS),
  favorites: load<GiphyGif[]>(STORAGE_KEYS.favorites, []),
  history: load<string[]>(STORAGE_KEYS.history, []),
  rating: RATINGS.includes(load<Rating>(STORAGE_KEYS.rating, DEFAULT_RATING))
    ? load<Rating>(STORAGE_KEYS.rating, DEFAULT_RATING)
    : DEFAULT_RATING,
  theme: load<Theme>(STORAGE_KEYS.theme, 'dark'),
  view: 'browse',
};

const listeners = new Set<Listener>();

function emit(): void {
  for (const fn of listeners) fn(state);
}

export const store = {
  get: (): Readonly<AppState> => state,

  subscribe(fn: Listener): void {
    listeners.add(fn);
  },

  // --- Quick-button animals -------------------------------------------------
  addAnimal(name: string): boolean {
    const trimmed = name.trim();
    if (!trimmed) return false;
    const exists = state.animals.some(
      (a) => a.toLowerCase() === trimmed.toLowerCase(),
    );
    if (exists) return false;
    state.animals.push(trimmed);
    persist(STORAGE_KEYS.animals, state.animals);
    emit();
    return true;
  },

  removeAnimal(name: string): void {
    state.animals = state.animals.filter((a) => a !== name);
    persist(STORAGE_KEYS.animals, state.animals);
    emit();
  },

  // --- Favorites ------------------------------------------------------------
  isFavorite(id: string): boolean {
    return state.favorites.some((g) => g.id === id);
  },

  toggleFavorite(gif: GiphyGif): void {
    if (this.isFavorite(gif.id)) {
      state.favorites = state.favorites.filter((g) => g.id !== gif.id);
    } else {
      state.favorites = [gif, ...state.favorites];
    }
    persist(STORAGE_KEYS.favorites, state.favorites);
    emit();
  },

  // --- Search history -------------------------------------------------------
  recordSearch(term: string): void {
    const trimmed = term.trim();
    if (!trimmed) return;
    state.history = [
      trimmed,
      ...state.history.filter((h) => h.toLowerCase() !== trimmed.toLowerCase()),
    ].slice(0, MAX_HISTORY);
    persist(STORAGE_KEYS.history, state.history);
    emit();
  },

  clearHistory(): void {
    state.history = [];
    persist(STORAGE_KEYS.history, state.history);
    emit();
  },

  // --- Preferences ----------------------------------------------------------
  setRating(rating: Rating): void {
    state.rating = rating;
    persist(STORAGE_KEYS.rating, rating);
    emit();
  },

  toggleTheme(): Theme {
    state.theme = state.theme === 'dark' ? 'light' : 'dark';
    persist(STORAGE_KEYS.theme, state.theme);
    emit();
    return state.theme;
  },

  setView(view: View): void {
    state.view = view;
    emit();
  },
};

export type { AppState, Theme, View };
